import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import propertyService from '../../services/propertyService';
import ImageUpload from '../common/ImageUpload';
import { societyService, extractSocietyResults } from '../../services/societyService';
import {
  saveFormData,
  loadFormData,
  clearFormData,
  pickPersistedFormFields,
} from '../../utils/formPersistence';
import LoginPromptModal from '../common/LoginPromptModal';
import SocietySearchableSelect from '../common/SocietySearchableSelect';

const PropertyCreateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    address: '',
    price: '',
    property_type: 'for_sale',
    size: '',
    size_unit: 'marla',
    contact_phone: '',
    society: '',
    is_public: false
  });
  const [userSociety, setUserSociety] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const formDraftRestoredRef = useRef(false);
  const societiesLoadedForUserRef = useRef(null);
  const userId = user?.id;
  const societyIdFromUrl = searchParams.get('society');

  // Restore saved draft once
  useEffect(() => {
    if (formDraftRestoredRef.current) {
      return;
    }
    formDraftRestoredRef.current = true;

    const savedFields = pickPersistedFormFields(loadFormData('property'));
    if (savedFields) {
      setFormData((prev) => ({
        ...prev,
        ...savedFields,
      }));
      setSelectedImages([]);
      showSuccess('Your previous form data has been restored!');
    }
  }, [showSuccess]);

  // Load societies once per user
  useEffect(() => {
    if (!userId || societiesLoadedForUserRef.current === userId) {
      return undefined;
    }

    let cancelled = false;

    const loadSocieties = async () => {
      try {
        setLoadingSocieties(true);

        const userIsAdmin =
          user.is_staff ||
          user.is_superuser ||
          ['admin', 'moderator'].includes(user.role);
        if (!cancelled) {
          setIsAdmin(userIsAdmin);
        }

        if (userIsAdmin) {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({
                ...prev,
                society: prev.society || selected.id,
              }));
            }
          }
        } else if (user.society) {
          if (cancelled) {
            return;
          }
          setUserSociety(user.society);
          setSocieties([user.society]);
          setFormData((prev) => ({
            ...prev,
            society: prev.society || user.society.id,
          }));
        } else {
          const response = await societyService.getSocieties({ page_size: 500 });
          const allSocieties = extractSocietyResults(response);
          if (cancelled) {
            return;
          }
          setSocieties(allSocieties);
          if (societyIdFromUrl) {
            const sid = parseInt(societyIdFromUrl, 10);
            const selected = allSocieties.find((s) => s.id === sid);
            if (selected) {
              setFormData((prev) => ({
                ...prev,
                society: prev.society || selected.id,
              }));
            }
          }
        }

        societiesLoadedForUserRef.current = userId;
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading societies:', error);
          showError('Failed to load societies. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSocieties(false);
        }
      }
    };

    loadSocieties();

    return () => {
      cancelled = true;
    };
  }, [userId, societyIdFromUrl, showError]);

  // Save form data whenever it changes
  useEffect(() => {
    if (formData.title || formData.description || formData.address) {
      saveFormData('property', formData);
    }
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    
    setLoading(true);

    try {
      const propertyData = {
        ...formData,
        images: selectedImages
      };
      
      await propertyService.createProperty(propertyData);
      
      // Clear saved form data on successful submission
      clearFormData('property');
      
      showSuccess('Property created successfully! It will be reviewed by admin before being published.');
      
      // Check if we're in a society context and navigate back appropriately
      const isSocietyContext = location.pathname.includes('/societies/');
      if (isSocietyContext) {
        // Extract society slug from the current path
        const pathParts = location.pathname.split('/');
        const societySlugIndex = pathParts.indexOf('societies') + 1;
        const societySlug = pathParts[societySlugIndex];
        navigate(`/societies/${societySlug}/properties`);
      } else {
        navigate('/properties');
      }
    } catch (error) {
      console.error('Error creating property:', error);
      showError('Failed to create property. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = () => {
    navigate('/?auth=login');
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const societySelect = (
    <SocietySearchableSelect
      societies={societies}
      value={formData.society}
      onChange={handleChange}
      loading={loadingSocieties}
    />
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create New Property</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Society: admins pick any; members with profile society are locked; others pick from list */}
          {isAdmin ? (
            <div>{societySelect}</div>
          ) : userSociety ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Society
              </label>
              <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                {`${userSociety.name} - ${userSociety.city}`}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                You can only create properties for your own society.
              </p>
            </div>
          ) : (
            <div>
              {societySelect}
              <p className="mt-1 text-xs text-gray-500">
                Select the society for this listing. Submissions are reviewed before publication.
              </p>
            </div>
          )}

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Property Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter property title"
            />
          </div>

          {/* Property Type */}
          <div>
            <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-2">
              Property Type *
            </label>
            <select
              id="property_type"
              name="property_type"
              value={formData.property_type}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="for_sale">For Sale</option>
              <option value="for_rent">For Rent</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Describe the property..."
            />
          </div>

          {/* Address */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter property address"
            />
          </div>

          {/* Price */}
          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
              Price (PKR)
            </label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter price in PKR"
            />
          </div>

          {/* Property Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-2">
                Property Size
              </label>
              <input
                type="number"
                id="size"
                name="size"
                value={formData.size}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 5.5"
              />
            </div>
            <div>
              <label htmlFor="size_unit" className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <select
                id="size_unit"
                name="size_unit"
                value={formData.size_unit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="marla">Marla</option>
                <option value="kanal">Kanal</option>
                <option value="square_feet">Square Feet</option>
                <option value="square_yards">Square Yards</option>
                <option value="square_meters">Square Meters</option>
                <option value="acres">Acres</option>
              </select>
            </div>
          </div>

          {/* Contact Phone */}
          <div>
            <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              id="contact_phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter contact phone number"
            />
          </div>

          {/* Images */}
          <ImageUpload
            selectedImages={selectedImages}
            onImagesChange={setSelectedImages}
            maxImages={15}
            disabled={loading}
            onError={(msg) => { if (msg) showError(msg); }}
          />

          {/* Public Visibility */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_public"
              name="is_public"
              checked={formData.is_public}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
              Make this property public (visible to everyone)
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                const isSocietyContext = location.pathname.includes('/societies/');
                if (isSocietyContext) {
                  const pathParts = location.pathname.split('/');
                  const societySlugIndex = pathParts.indexOf('societies') + 1;
                  const societySlug = pathParts[societySlugIndex];
                  navigate(`/societies/${societySlug}/properties`);
                } else {
                  navigate('/properties');
                }
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Property'}
            </button>
          </div>
        </form>

        {/* Info Message */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Your property will be reviewed by an admin before being published and visible to the public.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={handleCloseLoginModal}
        onLogin={handleLogin}
        title="Login Required to Create Property"
        message="You need to be logged in to create a property. Your form data has been saved and will be restored after login."
      />
    </div>
  );
};

export default PropertyCreateForm;
